import _ from 'lodash';
import { STAFF_PATHS } from '../../../../_configuration';
import React, { useEffect, useState } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import CircularProgress from '@material-ui/core/CircularProgress';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';

// Components
import { FormInput } from '../../_core/_ui/forms.component';
import { EnhancedTable, IEnhancedTableRow } from '../../_core/_ui/table.components';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { Spacer } from '../../_core/_ui/structure.components';
import ToggleGlobal from '../../User/ToggleGlobal/toggle-global.component';
import { Bold } from '../../_core/_ui/typography.component';
import { mainColor } from '../../../lib/theme';
import EditIcon from '@material-ui/icons/Edit';
import { generatePath, useHistory } from 'react-router-dom';
import { getAuthUser } from '../../../lib/services/auth.service';
import { DropFile } from '../../_core/DropFile/drop-file.component';
import { Modal } from '@material-ui/core';
import DrillListImageButton from './drill-list-image.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { getMessages } from './drill-list.messages';

interface IDrillList {
  drills: Array<any>;
  countArchivedDrills: number;
  searchDrills: Function;
  removeDrills: Function;
  restoreDrills: Function;
  loading: boolean;
  fetchError: boolean
  viewingArchived: boolean;
  setViewingArchived: Function;
  selectedDrills: IDrill[];
  setSelectedDrills: Function;
  removeDrillImage: Function;
  addDrillImage: Function;
  isAdmin: boolean;
}

export const DrillList: React.FC<IDrillList> = ({
  drills,
  countArchivedDrills,
  searchDrills,
  loading,
  fetchError,
  removeDrills,
  restoreDrills,
  viewingArchived,
  setViewingArchived,
  selectedDrills,
  setSelectedDrills,
  removeDrillImage,
  addDrillImage,
  isAdmin
}) => {
  const termLookup = useCompanySportTerminology()
  const messages = getMessages(termLookup);
  const eventsTerm = pluralTerm(termLookup[SportTerminology.EVENT]);

  const user = getAuthUser();
  const history = useHistory();
  const handleUploadError = useOperationErrorHandler(messages.errUploadImage);
  const [rows, setRows] = useState<IEnhancedTableRow[]>([]);
  const [busyDrillId, setBusyDrillId] = useState<string | null>(null);
  const [showDiagram, setShowDiagram] = useState<string | null>(null);

  const onReceivedFiles = (drillId: string, files: FileList) => {
    setBusyDrillId(drillId);
    addDrillImage(drillId, files, user?.token)
      .then(() => setBusyDrillId(null))
      .catch((err: Error) => {
        setBusyDrillId(null);
        handleUploadError('addDrillImage', err);
      });
  }

  useEffect(() => {
    const nextRows: IEnhancedTableRow[] = [];
    // One upload at a time; see "Upload buttons go busy at file-dialog open, not upload start"
    // in CLAUDE.md.
    const isUploadBlocked = Boolean(busyDrillId);

    _.each(drills, drill => {
      // Association drills are managed by anyone with Skills & Drills access (the page's own
      // route gate); global marketplace drills stay Max-admin-only.
      const canManageImage = Boolean(isAdmin || drill.companyId);

      nextRows.push({
        disableSelect: !drill.companyId && !isAdmin,
        _id: drill._id,
        data: drill,
        cells: [
          <TableCell key="actions" padding="checkbox">
            {(user?.isAdmin || drill.companyId) && (
              <IconButton onClick={() => history.push(generatePath(STAFF_PATHS.DRILL, { drillId: drill._id }))}>
                <EditIcon />
              </IconButton>
            )}
          </TableCell>,
          <TableCell key="name">{drill.name}</TableCell>,
          <TableCell key="skills">{
            drill.skillSets?.map((skillSet: ISkillSet, index: number) => {
              const positionNames = skillSet?.positions?.map(position => position.name).join(', ');
              const skillNames = skillSet?.skills?.filter(skill => !skill.deletedAt).map(skill => skill.name).join(', ');

              return <Typography key={index}><Bold style={{ color: mainColor }}>{positionNames}:</Bold> {skillNames}</Typography>;
            })
          }</TableCell>,
          <TableCell key="image">
            {!drill.image && canManageImage && (
              <IconButton disabled={isUploadBlocked}>
                <DropFile
                  disabled={isUploadBlocked}
                  onFileDialogOpen={() => setBusyDrillId(drill._id)}
                  onFileDialogCancel={() => setBusyDrillId(null)}
                  onReceivedFiles={(files: FileList) => onReceivedFiles(drill._id, files)}
                >
                  {busyDrillId === drill._id ? <CircularProgress size={24} /> : <AddPhotoAlternateIcon />}
                </DropFile>
              </IconButton>
            )}
            {!drill.image && !canManageImage && (
              <Tooltip title={<IntlMsg {...messages.lblNoDiagram} />}>
                <BrokenImageIcon style={{ color: '#777' }} />
              </Tooltip>
            )}
            {drill.image && <DrillListImageButton removeDrillImage={removeDrillImage} drill={drill} setShowDiagram={setShowDiagram} canManageImage={canManageImage} />}
          </TableCell>
        ]
      });
    });

    setRows(nextRows);
  // busyDrillId drives the per-row spinner and disables every image button while an upload
  // is picking or in flight, so the rows rebuild when it changes.
  }, [drills, isAdmin, busyDrillId]);



  const columns = [
    { key: 'actions', width: '60px', disableSort: true },
    { key: 'name', disablePadding: true, label: 'Name', path: 'name' },
    { key: 'skills', disablePadding: true, disableSort: true, label: pluralTerm(termLookup[SportTerminology.METRIC]) },
    { key: 'image', disablePadding: true, disableSort: true, label: 'Diagram' }
  ]

  return (
    <>
      <Modal
        open={Boolean(showDiagram)}
        onClose={() => setShowDiagram(null)}
        className="diagram-modal"
        >
        <Paper style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <Box p={2}>
            {showDiagram && <img src={showDiagram} style={{ minWidth: '400px', maxWidth: '100%' }} alt="Broken Image. Please re-upload" />}
          </Box>
        </Paper>
      </Modal>
      <EnhancedTable
        numCols={4}
        columns={columns}
        rows={rows}
        defaultOrder="name"
        entityName="drills"
        showError={fetchError}
        showLoading={loading}
        title={viewingArchived ? 'Archived ' + eventsTerm : eventsTerm}
        selected={selectedDrills}
        setSelected={setSelectedDrills}
        selectedAction={(
          <Tooltip title={viewingArchived ? 'Restore' : 'Delete'}>
            <IconButton aria-label="delete" onClick={() => {
              viewingArchived ? restoreDrills(selectedDrills) : removeDrills(selectedDrills);
              setSelectedDrills([]);
            }}>
              {viewingArchived ? <UnarchiveIcon /> : <DeleteIcon />}
            </IconButton>
          </Tooltip>
        )}
        filterComponent={(<>
          <Box mr={2} flexShrink={0}><ToggleGlobal label={"Show Max Analytics Marketplace " + eventsTerm} /></Box>
          <FormInput
            placeholder={viewingArchived ? `Search archived ${eventsTerm.toLowerCase()}...` : `Search ${eventsTerm}...`}
            onKeyUp={(e: any) => searchDrills(e.currentTarget.value)} />
        </>)}
      />
      {countArchivedDrills > 0 && !viewingArchived && (
        <Box display="flex">
          <Spacer />
          <SecondaryButton startIcon={<ArchiveIcon />} onClick={() => setViewingArchived(true)}>View Archived {eventsTerm}</SecondaryButton>
        </Box>
      )}
      {viewingArchived && (
        <Box display="flex">
          <Spacer />
          <PrimaryButton startIcon={<ArrowBackIosIcon />} onClick={() => setViewingArchived(false)}>Back to {eventsTerm}</PrimaryButton>
        </Box>
      )}
    </>
  );
};
